/**
 * ActivateDeploymentModal Component
 *
 * Modal for confirming deployment activation.
 * Activating a deployment makes it the current active deployment for the application.
 */

import React from 'react';
import { Modal } from '../../../components/ui/Modal';
import { Button } from '../../../components/ui/Button';
import { Alert } from '../../../components/ui/Alert';
import { Badge } from '../../../components/ui/Badge';
import type { DeploymentSanitized } from '../../../types/deployment.types';
import { useActivateDeployment } from '../hooks/useActivateDeployment';
import { getErrorMessage } from '../../../lib/utils/errors';

interface ActivateDeploymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  tenantId: string;
  applicationId: string;
  deployment: DeploymentSanitized;
  currentActiveDeploymentId?: string;
  onSuccess?: () => void;
}

/**
 * ActivateDeploymentModal component
 */
export const ActivateDeploymentModal: React.FC<ActivateDeploymentModalProps> = ({
  isOpen,
  onClose,
  tenantId,
  applicationId,
  deployment,
  currentActiveDeploymentId,
  onSuccess,
}) => {
  const activateMutation = useActivateDeployment({
    tenantId,
    applicationId,
    onSuccess: () => {
      onSuccess?.();
      onClose();
    },
  });

  const handleClose = () => {
    if (!activateMutation.isPending) {
      activateMutation.reset();
      onClose();
    }
  };

  const handleActivate = () => {
    activateMutation.mutate(deployment.deploymentId);
  };

  const isLoading = activateMutation.isPending;
  const error = activateMutation.error;
  const hasCurrentActive = !!currentActiveDeploymentId;

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="md">
      <Modal.Header>Activate Deployment</Modal.Header>

      <Modal.Body>
        <div className="space-y-4">
          {/* Error Alert */}
          {error && (
            <Alert variant="error">
              {getErrorMessage(error)}
            </Alert>
          )}

          {/* Warning */}
          <Alert variant="warning">
            <div className="space-y-2">
              <p className="font-semibold">This will switch the active deployment!</p>
              <p className="text-sm">
                {hasCurrentActive
                  ? 'The current active deployment will be replaced with this one. All traffic will be routed to the new deployment.'
                  : 'This deployment will become the active deployment for the application.'}
              </p>
            </div>
          </Alert>

          {/* Deployment Info */}
          <div className="bg-gray-50 rounded-lg p-4 space-y-3">
            <h4 className="text-sm font-semibold text-gray-900">New Active Deployment</h4>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">ID:</span>
                <span className="text-sm font-mono text-gray-900">
                  {deployment.deploymentId.slice(0, 8)}...
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Version:</span>
                <span className="text-sm text-gray-900">{deployment.version}</span>
              </div>
              {deployment.versionTag && (
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Version Tag:</span>
                  <Badge variant="info" size="sm">{deployment.versionTag}</Badge>
                </div>
              )}
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Status:</span>
                <Badge
                  variant={deployment.deploymentStatus === 'deployed' ? 'success' : 'neutral'}
                  size="sm"
                >
                  {deployment.deploymentStatus}
                </Badge>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Deployed:</span>
                <span className="text-sm text-gray-900">
                  {new Date(deployment.createdAt).toLocaleString()}
                </span>
              </div>
              {deployment.isLatest && (
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Latest:</span>
                  <Badge variant="warning" size="sm">LATEST</Badge>
                </div>
              )}
            </div>
          </div>

          {/* Custom Tags */}
          {deployment.customTags && deployment.customTags.length > 0 && (
            <div>
              <span className="text-sm font-medium text-gray-700">Custom Tags:</span>
              <div className="flex flex-wrap gap-2 mt-2">
                {deployment.customTags.map((tag) => (
                  <Badge key={tag} variant="neutral" size="sm">
                    {tag}
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </div>
      </Modal.Body>

      <Modal.Footer>
        <div className="flex justify-end gap-3">
          <Button
            variant="secondary"
            onClick={handleClose}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleActivate}
            disabled={isLoading}
            loading={isLoading}
          >
            Activate Deployment
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};
